import ReportChartPage, {
  ReportChartConfig,
} from '@app/components/reports/components/report-chart-page';
import {
  emissionsChartConfig,
  totalEmissionsAxisProps,
  totalWeightKgAxisProps,
  totalWeightKgChartConfig,
} from '@app/components/reports/config';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import DualAxisBarChart from '@shared/components/chart/dual-axis-bar-chart';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import {
  getXAxisInterval,
  truncateLabel,
} from '@shared/components/chart/utils';
import Text from '@shared/components/content/text';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { useState } from 'react';

interface EmissionsByMaterialProps extends ReportSectionComponent {}

export default function EmissionsByMaterial({
  reportType,
}: EmissionsByMaterialProps) {
  const {
    result: { packaging },
    downloadMode,
  } = useGetReportSectionData(reportType);
  const reportTypeCopy = useGetReportTypeCopy(reportType);

  const variants = {
    emissions: 'Emissions per material',
    weight: `Emissions vs ${reportTypeCopy.past} weight`,
  };

  const [variant, setVariant] = useState<string>(variants.emissions);

  const emissionsByMaterial = packaging?.emissionsByMaterial.map((item) => ({
    ...item,
    totalWeightKg: item.totalWeightKg / 1000,
  }));

  const emissionsDataMax = Math.max(
    0,
    ...(emissionsByMaterial?.map((d) => d.totalEmissionsTonnes) ?? [])
  );
  const weightDataMax = Math.max(
    0,
    ...(emissionsByMaterial?.map((d) => d.totalWeightKg) ?? [])
  );

  const totalEmissionsChartConfig = {
    totalEmissionsTonnes: emissionsChartConfig.emissionsTonnes,
  };

  const defaultChartProps = {
    nameKey: 'material' as const,
    dataKey: 'material' as const,
    margin: { top: 40, right: 10, left: 0, bottom: 40 },
    className: 'h-max max-h-[calc(var(--content-title-height)-20rem)] w-full',
    XAxisProps: {
      tickFormatter: (value: string) =>
        capitalizeFirstLetter(truncateLabel(value, 15)),
      interval: 0,
      angle: -30,
      textAnchor: 'end',
      tickMargin: 2,
      dx: 10,
    },
  };

  let config: ReportChartConfig = totalEmissionsChartConfig;
  if (variant === variants.weight) {
    config = {
      ...totalEmissionsChartConfig,
      ...totalWeightKgChartConfig,
    };
  }

  return (
    <ReportChartPage
      title="Emissions per material category"
      description={
        <Text variant="reportBody">
          This chart shows which types of material are contributing the most to
          your total emissions. The highest emissions categories are shown, with
          the remaining grouped into the <em>Other</em> category.
        </Text>
      }
      variantOptions={Object.values(variants)}
      variant={variant}
      setVariant={setVariant}
      variantsEnabled={!downloadMode}
      chartConfig={config}
    >
      {variant === variants.emissions && (
        <SimpleBarChart
          {...defaultChartProps}
          chartConfig={totalEmissionsChartConfig}
          chartData={emissionsByMaterial}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsByMaterial?.length ?? 0),
          }}
        />
      )}
      {variant === variants.weight && (
        <DualAxisBarChart
          {...defaultChartProps}
          legend={false}
          chartData={emissionsByMaterial}
          leftAxisConfig={totalEmissionsChartConfig}
          rightAxisConfig={totalWeightKgChartConfig(reportType)}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={totalWeightKgAxisProps(weightDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsByMaterial?.length ?? 0),
          }}
        />
      )}
    </ReportChartPage>
  );
}
